import React, { useContext } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { CommonContext } from '../../context/CommonContext';

/**
 * AlertSuccess - A dismissible snackbar alert component that appears in the top-right corner.
 * It uses snackState from CommonContext to control visibility, message, and severity.
 */
const AlertSuccess = () => {
  const { snackState, setSnackState } = useContext(CommonContext);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackState((prev) => ({ ...prev, open: false }));
  };

  // Define custom styles for success and error states
  const getAlertStyles = () => {
    if (snackState.severity === 'success') {
      return {
        backgroundColor: '#ebffeb', // Very light green
        color: '#1b5e20',
        border: '1px solid #c8e6c9',
        fontWeight: 500,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: '8px',
        '& .MuiAlert-icon': {
          color: '#2e7d32',
        },
      };
    } else if (snackState.severity === 'error') {
      return {
        backgroundColor: '#fff1f1', // Very light red
        color: '#c62828',
        border: '1px solid #ffcdd2',
        fontWeight: 500,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: '8px',
        '& .MuiAlert-icon': {
          color: '#d32f2f',
        },
      };
    } else if (snackState.severity === 'warning') {
      return {
        backgroundColor: '#fff8e1', // Very light yellow
        color: '#f57c00',
        border: '1px solid #ffecb3',
        fontWeight: 500,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: '8px',
        '& .MuiAlert-icon': {
          color: '#ef6c00',
        },
      };
    } else if (snackState.severity === 'info') {
      return {
        backgroundColor: '#e3f2fd', // Very light blue
        color: '#1565c0',
        border: '1px solid #bbdefb',
        fontWeight: 500,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: '8px',
        '& .MuiAlert-icon': {
          color: '#1976d2',
        },
      };
    }
    return {};
  };

  return (
    <Snackbar
      open={snackState.open}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ mt: 7, mr: 2 }} // Added margin top to avoid overlap with header if any
    >
      <Alert
        onClose={handleClose}
        severity={snackState.severity}
        variant="filled" // We'll override the filled variant with our custom colors
        sx={{
          width: '300px', // Fixed width
          wordBreak: 'break-word', // Ensure long text wraps
          display: 'flex',
          alignItems: 'center',
          ...getAlertStyles(),
          '& .MuiAlert-action': {
            paddingTop: 0,
            paddingBottom: 0,
            alignItems: 'center'
          }
        }}
      >
        {snackState.message}
      </Alert>
    </Snackbar>
  );
};

export default AlertSuccess;